import Text from '@/storybook/components/Text/Text';
import cx from 'classnames';
import { FC, MouseEvent, ReactNode, useState } from 'react';
import styles from '../PageBanner.module.css';
import { PAGE_BANNER_TEST_IDS } from '../testIds';
import { BannerSlotKeys, TITLE_MEASURE_ATTRIBUTE } from '../types';
import { Slot, SlotContentProps } from './Slot';

// Scroll/client sizes are whole pixels, so 1px of overflow may be subpixel rounding (zoom, high-DPI).
const ROUNDING_TOLERANCE_PX = 1;

const TITLE_MEASURE_PROPS = { [TITLE_MEASURE_ATTRIBUTE]: '' };

interface TitleSlotProps extends SlotContentProps {
  clamped?: boolean;
  underlined?: boolean;
  titleAccessory?: ReactNode;
}

const Title: FC<TitleSlotProps> = ({ clamped, underlined, titleAccessory, children }) => {
  const [truncatedTitle, setTruncatedTitle] = useState<string>();

  // Measured on hover because the line clamp changes with scroll state and viewport width.
  const handleTitleTextMouseEnter = ({ currentTarget: titleText }: MouseEvent<HTMLSpanElement>) => {
    const isTruncated =
      titleText.scrollHeight - titleText.clientHeight > ROUNDING_TOLERANCE_PX ||
      titleText.scrollWidth - titleText.clientWidth > ROUNDING_TOLERANCE_PX;
    setTruncatedTitle(isTruncated ? titleText.textContent || undefined : undefined);
  };

  const handleTitleTextMouseLeave = () => setTruncatedTitle(undefined);

  return (
    <Slot id={BannerSlotKeys.Title}>
      <Text as="h1" className={cx(styles.title, { [styles.clamped]: clamped, [styles.underlined]: underlined })}>
        <Text
          as="span"
          className={styles.titleText}
          title={truncatedTitle}
          onMouseEnter={handleTitleTextMouseEnter}
          onMouseLeave={handleTitleTextMouseLeave}
          data-testid={PAGE_BANNER_TEST_IDS.titleText}
        >
          {children}
        </Text>
        {titleAccessory && <span className={styles.titleAccessory}>{titleAccessory}</span>}
        <Text
          as="span"
          className={styles.titleMeasure}
          aria-hidden="true"
          data-testid={PAGE_BANNER_TEST_IDS.titleMeasure}
          {...TITLE_MEASURE_PROPS}
        >
          {children}
        </Text>
      </Text>
    </Slot>
  );
};

export default Title;
